import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";

const CurrencyContext = createContext(null);

const FALLBACK = {
  rates: { USD: 1.0, INR: 83.2, EUR: 0.92 },
  symbols: { USD: "$", INR: "₹", EUR: "€" },
  source: "fallback",
  fetched_at: null,
};

export function CurrencyProvider({ children }) {
  const [code, setCode] = useState(() => localStorage.getItem("spv_currency") || "USD");
  const [meta, setMeta] = useState(FALLBACK);
  const [lock, setLock] = useState(() => {
    try {
      const raw = localStorage.getItem("spv_rate_lock");
      if (!raw) return null;
      const l = JSON.parse(raw);
      if (new Date(l.expires_at) < new Date()) {
        localStorage.removeItem("spv_rate_lock");
        return null;
      }
      return l;
    } catch { return null; }
  });

  const refreshRates = async () => {
    try {
      const r = await axios.get(`${API}/currency/rates`);
      setMeta(r.data);
    } catch {}
  };

  useEffect(() => { refreshRates(); const t = setInterval(refreshRates, 60_000); return () => clearInterval(t); }, []);

  // Auto-clear expired lock
  useEffect(() => {
    if (!lock) return;
    const ms = new Date(lock.expires_at) - new Date();
    if (ms <= 0) { clearLock(); return; }
    const t = setTimeout(() => clearLock(), ms);
    return () => clearTimeout(t);
  }, [lock?.lock_id]);

  const changeCurrency = (c) => {
    setCode(c);
    localStorage.setItem("spv_currency", c);
    // If current lock is for a different currency, drop it
    if (lock && lock.currency !== c) clearLock();
  };

  const getRate = (c = code) => {
    // If lock exists for this currency, use locked rate
    if (lock && lock.currency === c && new Date(lock.expires_at) > new Date()) return lock.rate;
    return meta.rates[c] || 1;
  };

  const format = (usd, opts = {}) => {
    const c = opts.currency || code;
    const rate = getRate(c);
    const symbol = meta.symbols[c] || "$";
    const val = Number(usd) * rate;
    const digits = opts.digits ?? (c === "INR" ? 0 : 2);
    return `${symbol}${val.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  };

  const requestLock = async (amountUsd, authHeaders) => {
    const r = await axios.post(
      `${API}/currency/lock`,
      { currency: code, amount_usd: Number(amountUsd) },
      { headers: authHeaders?.() || {}, withCredentials: true }
    );
    setLock(r.data);
    localStorage.setItem("spv_rate_lock", JSON.stringify(r.data));
    return r.data;
  };

  const clearLock = () => {
    setLock(null);
    localStorage.removeItem("spv_rate_lock");
  };

  return (
    <CurrencyContext.Provider
      value={{
        code,
        changeCurrency,
        format,
        getRate,
        rates: meta.rates,
        symbols: meta.symbols,
        source: meta.source,
        fetchedAt: meta.fetched_at,
        lock,
        requestLock,
        clearLock,
        refreshRates,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
